import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../lib/supabase';
import { toISODateChile } from '../../styles/tokens/formatters';

const PAGE_SIZE = 50;

export default function PolizasIndividualesList() {
  const { session } = useSession();
  const supabase    = useMemo(() => createClerkSupabaseClient(session), [session]);
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [errMsg,     setErrMsg]     = useState(null);
  const [search,     setSearch]     = useState('');
  const [companiaId, setCompaniaId] = useState('');
  const [companias,  setCompanias]  = useState([]);
  const [page,       setPage]       = useState(0);
  const [total,      setTotal]      = useState(0);

  const cargarCompanias = useCallback(async () => {
    const { data } = await supabase
      .from('companias')
      .select('id,nombre_corto')
      .order('nombre_corto');
    setCompanias(data ?? []);
  }, [supabase]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErrMsg(null);

    try {
      let polizasQuery = supabase
        .from('polizas')
        .select('id, numero_poliza, numero_propuesta, estado, fecha_inicio, fecha_fin, prima_anual_uf, fecha_recepcion, certificado_id', { count: 'exact' })
        .order('fecha_recepcion', { ascending: false });

      if (companiaId) polizasQuery = polizasQuery.eq('compania_id', companiaId);

      if (search.trim()) {
        const s = search.trim();
        // company_certificados es una vista — buscar cert fields por separado
        const { data: certMatches } = await supabase
          .from('company_certificados')
          .select('id')
          .or(`cliente_rut.ilike.%${s}%,cliente_nombre.ilike.%${s}%,codigo.ilike.%${s}%`)
          .limit(500);

        const certIds = certMatches?.map(c => c.id) ?? [];
        if (certIds.length > 0) {
          polizasQuery = polizasQuery.or(
            `numero_poliza.ilike.%${s}%,certificado_id.in.(${certIds.join(',')})`,
          );
        } else {
          polizasQuery = polizasQuery.ilike('numero_poliza', `%${s}%`);
        }
      }

      const { data: polizas, error, count } = await polizasQuery
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      if (error) throw error;

      // Batch-fetch datos de la vista para las pólizas retornadas
      const certIds = (polizas ?? []).map(p => p.certificado_id).filter(Boolean);
      let certMap = {};
      if (certIds.length > 0) {
        const { data: certs } = await supabase
          .from('company_certificados')
          .select('id, codigo, cliente_nombre, cliente_rut, plan_nombre, compania_nombre, compania_id')
          .in('id', certIds);
        certMap = Object.fromEntries((certs ?? []).map(c => [c.id, c]));
      }

      setRows((polizas ?? []).map(p => ({ ...p, company_certificados: certMap[p.certificado_id] ?? null })));
      setTotal(count ?? 0);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setLoading(false);
    }
  }, [supabase, page, companiaId, search]);

  useEffect(() => { if (session) cargarCompanias(); }, [cargarCompanias, session]);
  useEffect(() => { if (session) cargar(); }, [cargar, session]);

  function handleSearch(e) { setSearch(e.target.value); setPage(0); }
  function handleCompania(e) { setCompaniaId(e.target.value); setPage(0); }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900">Pólizas Individuales</h1>
        <p className="text-sm text-gray-500 mt-1">
          Pólizas emitidas por las compañías aseguradoras para certificados individuales.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <input
          type="text"
          placeholder="Buscar por N° póliza, RUT, nombre o código…"
          value={search}
          onChange={handleSearch}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={companiaId}
          onChange={handleCompania}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todas las compañías</option>
          {companias.map(c => (
            <option key={c.id} value={c.id}>{c.nombre_corto}</option>
          ))}
        </select>
        <span className="self-center text-sm text-gray-500">
          {total} póliza{total !== 1 ? 's' : ''}
        </span>
      </div>

      {errMsg && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {errMsg}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm py-10 text-center">Cargando…</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">N° Póliza</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">RUT</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Compañía</th>
                  <th className="px-4 py-3">Código cert.</th>
                  <th className="px-4 py-3">Inicio</th>
                  <th className="px-4 py-3">Término</th>
                  <th className="px-4 py-3">Prima UF</th>
                  <th className="px-4 py-3">Recibida</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const cc = r.company_certificados;
                  return (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700">
                        {r.numero_poliza}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{cc?.cliente_nombre}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{cc?.cliente_rut}</td>
                      <td className="px-4 py-3 text-gray-700">{cc?.plan_nombre}</td>
                      <td className="px-4 py-3 text-gray-700">{cc?.compania_nombre}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{cc?.codigo}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {r.fecha_inicio?.slice(0, 10) ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {r.fecha_fin?.slice(0, 10) ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {r.prima_anual_uf != null ? `${r.prima_anual_uf} UF` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {r.fecha_recepcion ? toISODateChile(r.fecha_recepcion) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-gray-400 text-sm">
                      Sin pólizas emitidas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center gap-3 justify-end">
              <button
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                ← Anterior
              </button>
              <span className="text-sm text-gray-500">
                Página {page + 1} de {totalPages}
              </span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
